import { WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { captureException } from '@/wabunka-src/common/utils/sentry';

export interface ConvertedPrice {
  amount: number;
  currency: string;
}

/**
 * Algolia 検索結果用の「表示通貨での価格」を遅延取得する composable。
 *
 * Algolia レコードは JPY のみ保持しているため、ユーザーが JPY 以外を選択している場合は、
 * Algolia の hits から得た slug 群をまとめて公開 API `/experiences/search?slugs=...&currency=...`
 * に投げて、`currencyConvertedPrice` を slug 単位で受け取る。
 *
 * - currency が JPY のときはリクエストを発生させない（JPY 表示で十分）
 * - slugs が空のときもリクエストしない
 * - slugs / currency の変化を watch し、stale なレスポンスは破棄（abort トークン）
 *
 * @see docs/Design Doc/キーワード検索.md
 */
export const useConvertedExperiencePricesQuery = (slugs: Ref<string[]>) => {
  const { selectedCurrency } = useCurrency();

  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const baseURL = runtimeConfig.public.WABUNKA_PUBLIC_API_BASE_URL as string;

  const priceBySlug = ref<Record<string, ConvertedPrice>>({});
  const isLoading = ref(false);

  // 通貨切替 / hits 変更で stale な fetch を破棄するためのトークン
  let activeToken = 0;

  watch(
    [slugs, selectedCurrency],
    async ([currentSlugs, currentCurrency]) => {
      // JPY は変換不要、slugs 空はリクエスト不要
      if (currentCurrency === 'JPY' || currentSlugs.length === 0) {
        priceBySlug.value = {};
        isLoading.value = false;
        return;
      }

      const token = ++activeToken;
      isLoading.value = true;
      try {
        const response = await $fetch('/experiences/search', {
          baseURL,
          headers: { Accept: 'application/json' },
          query: {
            slugs: currentSlugs.join(','),
            currency: currentCurrency,
            perPage: '0', // 全件
          },
        });

        // この間に新しいリクエストが走っていた場合は結果を捨てる
        if (token !== activeToken) return;

        const parsed = WabunkaExperienceListResponse.fromJSON(response);
        const next: Record<string, ConvertedPrice> = {};
        for (const item of parsed.items ?? []) {
          if (item.slug && item.currencyConvertedPrice) {
            next[item.slug] = {
              amount: item.currencyConvertedPrice.amount,
              currency: item.currencyConvertedPrice.currency,
            };
          }
        }
        priceBySlug.value = next;
      } catch (error) {
        if (token !== activeToken) return;
        // 換算失敗時は JPY 表示にフォールバック。UX 優先で Sentry には通知のみ
        captureException(error instanceof Error ? error : new Error(String(error)), 'wabunka');
        priceBySlug.value = {};
      } finally {
        if (token === activeToken) isLoading.value = false;
      }
    },
    { immediate: true },
  );

  return { priceBySlug, isLoading };
};
